import React, { useMemo, useState } from 'react';
import { View, StyleSheet, Text as RNText, Image, Pressable, StyleProp, TextStyle } from 'react-native';
import {
  Bevel,
  Text,
  colors,
  fontSizes,
  fonts,
  makeStyles,
  useTheme,
} from '@ogaim/ui';
import { parseRichText, isImageSpan, type Span } from '@ogaim/core';

export interface RichTextProps {
  body: string;
  /** Prefix rendered inline before the first span, e.g. "Bob: ". */
  prefix?: React.ReactNode;
  /** Appended after the last span, e.g. a timestamp. */
  suffix?: React.ReactNode;
  baseStyle?: StyleProp<TextStyle>;
}

/** Widest an inline image is drawn; taller ones scale to fit. */
const MAX_IMAGE_WIDTH = 260;
const MAX_IMAGE_HEIGHT = 220;

/**
 * Renders a message body.
 *
 * Text runs are grouped into a single <Text> so they wrap as prose, while
 * images become block elements between those groups — an image inside a <Text>
 * cannot be sized reliably across platforms.
 */
export function RichText({ body, prefix, suffix, baseStyle }: RichTextProps) {
  const styles = useStyles();
  const blocks = useMemo(() => groupSpans(parseRichText(body)), [body]);

  return (
    <View style={styles.root}>
      {blocks.map((block, i) =>
        block.kind === 'images' ? (
          <View key={i} style={styles.imageRow}>
            {block.spans.map((img, j) => (
              <InlineImage key={`${img.src}-${j}`} src={img.src} width={img.width} height={img.height} />
            ))}
          </View>
        ) : (
          <RNText key={i} style={[styles.text, baseStyle]}>
            {i === 0 && prefix ? prefix : null}
            {block.spans.map((s, j) => (
              <RNText
                key={j}
                style={[
                  styles.text,
                  baseStyle,
                  s.bold && styles.bold,
                  s.italic && styles.italic,
                  s.underline && styles.underline,
                  s.color ? { color: s.color } : null,
                  s.size ? { fontSize: s.size } : null,
                ]}
              >
                {s.text}
              </RNText>
            ))}
            {i === blocks.length - 1 && suffix ? suffix : null}
          </RNText>
        )
      )}
    </View>
  );
}

/**
 * One image, in a sunken well. Tapping toggles between fitted and full size,
 * which is the closest thing to AIM's click-to-expand.
 */
function InlineImage({ src, width, height }: { src: string; width?: number; height?: number }) {
  const theme = useTheme();
  const styles = useStyles();
  const [failed, setFailed] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Scale to fit the bubble while preserving the declared aspect ratio. With
  // no declared size, fall back to a 4:3 box until the image itself loads.
  const ratio = width && height ? width / height : 4 / 3;
  const boxWidth = expanded ? MAX_IMAGE_WIDTH * 1.6 : MAX_IMAGE_WIDTH;
  const fitted = {
    width: Math.min(boxWidth, width ?? boxWidth),
    height: Math.min(expanded ? MAX_IMAGE_HEIGHT * 1.6 : MAX_IMAGE_HEIGHT, (width ?? boxWidth) / ratio),
  };

  if (failed) {
    return (
      <Bevel variant="sunken" background={theme.colors.face} style={styles.imageFailed} contentStyle={styles.imageFailedInner}>
        <Text disabled style={styles.imageFailedText}>
          Image unavailable
        </Text>
      </Bevel>
    );
  }

  return (
    <Pressable
      onPress={() => setExpanded((v) => !v)}
      accessibilityRole="imagebutton"
      accessibilityLabel={expanded ? 'Image, tap to shrink' : 'Image, tap to enlarge'}
    >
      <Bevel variant="sunken" background={theme.colors.fieldBg} contentStyle={styles.imageInner}>
        <Image
          source={{ uri: src }}
          style={{ width: fitted.width, height: fitted.height }}
          resizeMode="contain"
          onError={() => setFailed(true)}
          accessible
          accessibilityLabel="Attached image"
        />
      </Bevel>
    </Pressable>
  );
}

type Block =
  | { kind: 'text'; spans: Array<Extract<Span, { text: string }>> }
  | { kind: 'images'; spans: Array<Extract<Span, { kind: 'image' }>> };

/** Collapses a flat span list into alternating text and image runs. */
function groupSpans(spans: Span[]): Block[] {
  const blocks: Block[] = [];
  for (const span of spans) {
    const want = isImageSpan(span) ? 'images' : 'text';
    const last = blocks[blocks.length - 1];
    if (last?.kind === want) {
      (last.spans as Span[]).push(span);
    } else {
      blocks.push({ kind: want, spans: [span] } as Block);
    }
  }
  return blocks;
}

const useStyles = makeStyles((t) => ({
  root: { gap: 3 },
  text: {
    fontFamily: t.fonts.message,
    fontSize: t.fontSizes.message,
    color: t.colors.windowText,
    lineHeight: t.fontSizes.message + 4,
  },
  bold: { fontWeight: 'bold' },
  italic: { fontStyle: 'italic' },
  underline: { textDecorationLine: 'underline' },

  imageRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 4, paddingVertical: 2 },
  imageInner: { padding: 2 },
  imageFailed: { width: 140, height: 44 },
  imageFailedInner: { alignItems: 'center', justifyContent: 'center' },
  imageFailedText: { fontSize: 10 },
}));
